import { Box, HBox, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import { Typo } from "@ui/text/Typo"
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { GD } from "src/GD"
import { getKycStatusSnapshot } from "src/utils/kyc"
import { useOptionalIssuer } from "../issuer/IssuerProvider"
import { DS } from "../ds/Kit"

// ---- Shared KYC building blocks (used by SimplifiedKyc, KycSection and the status card) ----

export type KycTone = "pending" | "approved" | "declined" | "expired" | "error" | "info"

const KYC_TONES: Record<KycTone, { bg: string; fg: string; line: string; glyph: ReactNode }> = {
	approved: { bg: "#ECFDF5", fg: "#047857", line: "#A7F3D0", glyph: <path d='m6 12.5 4 4L18.5 8' /> },
	pending: { bg: "#FFFBEB", fg: "#B45309", line: "#FDE68A", glyph: <><circle cx='12' cy='12' r='8.5' /><path d='M12 7.5V12l3 2' /></> },
	expired: { bg: "#FFFBEB", fg: "#B45309", line: "#FDE68A", glyph: <><circle cx='12' cy='12' r='8.5' /><path d='M12 7.5V12l3 2' /></> },
	declined: { bg: "#FEF2F2", fg: "#B91C1C", line: "#FECACA", glyph: <path d='m7.5 7.5 9 9M16.5 7.5l-9 9' /> },
	error: { bg: "#FEF2F2", fg: "#B91C1C", line: "#FECACA", glyph: <><path d='M12 7.5v5' /><path d='M12 16.5h.01' /></> },
	info: { bg: DS.accentSoft, fg: DS.accent, line: "#DDE1FF", glyph: <><path d='M12 11v5' /><path d='M12 7.5h.01' /></> }
}

export const kycToneForStatus = (status?: string | null): KycTone =>
	status === "approved" ? "approved"
		: status === "declined" ? "declined"
		: status === "expired" ? "expired"
		: status === "error" ? "error"
		: status === "pending" || status === "under_review" ? "pending"
		: "info"

/** Tinted status block with an icon circle: the one way KYC states are announced. */
export const KycCallout = ({ tone, title, children, action }: { tone: KycTone; title: ReactNode; children?: ReactNode; action?: ReactNode }) => {
	const colors = KYC_TONES[tone]
	return (
		<HBox gap='14px' sx={{ alignItems: "flex-start", padding: "16px 18px", borderRadius: DS.radius, border: `1px solid ${colors.line}`, background: colors.bg }}>
			<Box aria-hidden sx={{ width: "32px", height: "32px", minWidth: "32px", borderRadius: "50%", background: colors.fg, color: "#FFFFFF", display: "grid", placeItems: "center" }}>
				<svg width='17' height='17' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round'>{colors.glyph}</svg>
			</Box>
			<VBox gap='4px' sx={{ minWidth: 0, flex: 1 }}>
				<Typo sx={{ color: DS.ink, fontSize: "15px", fontWeight: 650, lineHeight: 1.35 }}>{title}</Typo>
				{children ? <Typo sx={{ color: DS.text, fontSize: "13px", lineHeight: 1.5, overflowWrap: "anywhere" }}>{children}</Typo> : null}
			</VBox>
			{action}
		</HBox>
	)
}

const KYC_STEPS = ["Phone", "Identity check", "Issuer review", "Done"] as const

/** Guided-flow header: Phone, Identity check, Issuer review, Done. `current` is the active step index. */
export const KycStepper = ({ current }: { current: 0 | 1 | 2 | 3 }) => (
	<Box role='list' aria-label='Verification progress' sx={{ display: "flex" }}>
		{KYC_STEPS.map((label, index) => {
			const done = index < current
			const active = index === current
			const reached = index <= current
			return (
				<Box
					key={label}
					role='listitem'
					aria-current={active ? "step" : undefined}
					sx={{
						position: "relative",
						flex: "1 1 0",
						minWidth: 0,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: "6px",
						"&:not(:first-of-type)::before": { content: '""', position: "absolute", top: "13px", right: "50%", width: "100%", height: "2px", background: reached ? DS.accent : DS.line }
					}}
				>
					<Box
						aria-hidden
						sx={{
							position: "relative",
							zIndex: 1,
							width: "28px",
							height: "28px",
							borderRadius: "50%",
							display: "grid",
							placeItems: "center",
							fontSize: "12px",
							fontWeight: 700,
							boxSizing: "border-box",
							background: done ? DS.accent : active ? DS.surface : DS.soft,
							color: done ? "#FFFFFF" : active ? DS.accent : DS.faint,
							border: `2px solid ${done || active ? DS.accent : DS.line}`,
							boxShadow: active ? "0 0 0 4px rgba(30, 58, 95, 0.14)" : "none"
						}}
					>
						{done ? <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3' strokeLinecap='round' strokeLinejoin='round'><path d='m5 12.5 4.5 4.5L19 7.5' /></svg> : index + 1}
					</Box>
					<Typo sx={{ fontSize: "12px", lineHeight: 1.3, textAlign: "center", fontWeight: active ? 650 : 500, color: active ? DS.ink : DS.muted }}>{label}</Typo>
				</Box>
			)
		})}
	</Box>
)

/** Verification block: QR on the left, instructions and the primary action on the right (stacked on mobile). */
export const KycVerifyCard = ({ qrDataUrl, qrSize = 168, hint, children }: { qrDataUrl?: string; qrSize?: number; hint?: string; children: ReactNode }) => {
	if (!qrDataUrl) return <>{children}</>
	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: "auto minmax(0, 1fr)",
				gap: "20px",
				alignItems: "center",
				padding: "18px",
				borderRadius: DS.radius,
				border: `1px solid ${DS.line}`,
				background: DS.surface,
				boxShadow: DS.shadow,
				[DS.mobile]: { gridTemplateColumns: "minmax(0, 1fr)", justifyItems: "center", textAlign: "center" }
			}}
		>
			<Box sx={{ padding: "10px", borderRadius: "14px", background: "#FFFFFF", border: `1px solid ${DS.line}`, boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)", lineHeight: 0 }}>
				<img src={qrDataUrl} alt='KYC verification QR code' width={qrSize} height={qrSize} />
			</Box>
			<VBox gap='14px' sx={{ minWidth: 0, width: "100%" }}>
				{hint ? <Typo sx={{ color: DS.text, fontSize: "14px", lineHeight: 1.5 }}>{hint}</Typo> : null}
				{children}
			</VBox>
		</Box>
	)
}

// ---- Provider status card ----

type ProviderPresentation = {
	tone: KycTone
	title: string
	description: string
}

const getProviderKycPresentation = (
	status: TProviderKycStatus | null,
	reason?: string | null,
	stage: TKycStage = "issuer"
): ProviderPresentation => {
	if (stage === "ready") {
		return {
			tone: "info",
			title: "Identity verification complete",
			description: "Complete the card holder details before creating a card."
		}
	}

	if (status === "approved") {
		return {
			tone: "approved",
			title: "Provider KYC approved",
			description: "Verification is complete. You can create cards that require it."
		}
	}

	if (status === "declined" || status === "error") {
		return {
			tone: status,
			title: stage === "our_kyc"
				? (status === "declined" ? "Identity verification declined" : "Identity verification error")
				: (status === "declined" ? "Provider KYC declined" : "Provider KYC error"),
			description: reason || (stage === "our_kyc"
				? "Identity verification was not completed. Try again or contact support."
				: "The provider could not verify your identity. Contact support.")
		}
	}

	if (status === "expired") {
		return {
			tone: "expired",
			title: stage === "our_kyc" ? "Identity verification expired" : "Provider KYC expired",
			description: reason || (stage === "our_kyc"
				? "The identity verification session expired. Start a new one."
				: "Provider verification expired. Verify your identity again.")
		}
	}

	return {
		tone: "pending",
		title: stage === "our_kyc"
			? (status === "none" ? "Identity verification not started" : "Identity verification in progress")
			: (status === "none" ? "Provider KYC not started" : "Provider KYC under review"),
		description:
			reason ||
			(stage === "our_kyc"
				? "Complete identity verification to continue."
				: "The card provider is checking your identity. This may take some time.")
	}
}

export const ProviderKycStatus = ({
	status,
	reason,
	stage = "issuer",
	loading = false,
	refreshing = false,
	onRefresh
}: {
	status: TProviderKycStatus | null
	reason?: string | null
	stage?: TKycStage
	loading?: boolean
	refreshing?: boolean
	onRefresh?: () => void
}) => {
	const presentation = getProviderKycPresentation(status, reason, stage)

	return (
		<KycCallout
			tone={loading ? "info" : presentation.tone}
			title={loading ? "Checking provider KYC…" : presentation.title}
			action={onRefresh ? (
				<Button
					title={refreshing ? "Checking…" : "Refresh"}
					variant="secondary"
					disabled={refreshing}
					onClick={onRefresh}
					sx={{ padding: "6px 14px", fontSize: "0.8rem", flexShrink: 0, borderRadius: "10px", "@media (max-width: 768px)": { minHeight: "44px", padding: "8px 12px" } }}
				/>
			) : undefined}
		>
			{loading ? null : presentation.description}
		</KycCallout>
	)
}

export const CurrentProviderKycStatus = () => {
	const issuer = useOptionalIssuer()?.selectedIssuerId ?? undefined
	return <IssuerProviderKycStatus key={issuer ?? "default"} issuer={issuer} />
}

const IssuerProviderKycStatus = ({ issuer }: { issuer?: number }) => {
	const [status, setStatus] = useState<TProviderKycStatus | null>(null)
	const [reason, setReason] = useState<string | null>(null)
	const [stage, setStage] = useState<TKycStage>("our_kyc")
	const [loading, setLoading] = useState(true)
	const [refreshing, setRefreshing] = useState(false)
	const mounted = useRef(false)

	const refresh = useCallback(async () => {
		setRefreshing(true)
		const res = await GD.REQ_KYC_STATUS.request({ issuer })
		if (!mounted.current) return
		if (res.data) {
			const snapshot = getKycStatusSnapshot(res.data)
			const nextStage = res.data.stage ?? (snapshot.ourStatus === "approved" ? "issuer" : "our_kyc")
			setStage(nextStage)
			setStatus(nextStage === "our_kyc" ? snapshot.ourStatus : snapshot.providerStatus)
			setReason(nextStage === "our_kyc" ? snapshot.ourReason : snapshot.providerReason)
		}
		setLoading(false)
		setRefreshing(false)
	}, [issuer])

	useEffect(() => {
		mounted.current = true
		void refresh()
		return () => {
			mounted.current = false
		}
	}, [refresh])

	return (
		<ProviderKycStatus
			status={status}
			reason={reason}
			stage={stage}
			loading={loading}
			refreshing={refreshing}
			onRefresh={() => void refresh()}
		/>
	)
}
